import type { CatalogueItemResource, SaleReprintResource } from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';

import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { labelForPaymentMode } from './payment-modes';
import { SaleTaxSection } from './SaleTax';

/**
 * The duplicate that came back from `POST /v1/sales/{id}/reprint`.
 *
 * **A receipt is reprintable, and that is deliberately the opposite of a ticket**
 * (FR-414, against CL-001). A ticket admits somebody, so a second copy is a free
 * admission and the whole numbering design exists to prevent one; a receipt is a
 * record of a payment already taken, and a customer who lost theirs is asking for
 * the record rather than for another entry.
 *
 * Three things are true of every reprint and each is stated on screen, because
 * each is a thing somebody will otherwise assume wrongly:
 *
 * - **It consumes no receipt number.** The duplicate carries the number the
 *   original was printed with (FR-414c). No counter moved.
 * - **It changes nothing about the sale.** Not the row, not the ledger, not the
 *   day's takings.
 * - **It is marked a duplicate**, which is what stops one being passed off as a
 *   second purchase. `duplicate` is `true` on the wire and the only value the
 *   type admits — a response without it would be indistinguishable from a first
 *   receipt, which is the one thing a reprint must never be. So the mark below is
 *   drawn from the field rather than from the fact that this component was
 *   rendered.
 *
 * **Who reprinted it is not sent and cannot be.** The API takes the person from
 * the authenticated principal and writes the audit row itself (FR-414a, FR-131);
 * there is no field on the request for one, and nothing here has anything to fill
 * it with.
 *
 * ## Why there is no print button
 *
 * Paper is a terminal's job. Receipts are printed as ESC/POS from the Electron
 * main process (`apps/desktop/src/main/printing`), and the back office has no
 * receipt printer and no bridge to one — the desktop shell is where that lives,
 * and a browser cannot reach it. This screen therefore produces the artefact and
 * marks it; getting it onto paper is the browser's own print, or a till's, and
 * inventing a print path here would mean an A4 sheet nobody asked for.
 *
 * A till reaches the same route, which is worth knowing rather than inferring.
 * `blossom-rest-api#411` (24 Aug 2026) gave it a second security alternative —
 * a terminal lease with an acting staff code — rather than a
 * `/v1/terminals/me/…` twin (R-412), so the two principals produce the same
 * duplicate and the same audit rule. What differs is the paper: the desktop
 * shell prints it as ESC/POS from the main process
 * (`apps/desktop/src/main/sales/reprint-document.ts`), which is why the till
 * has a control and this screen has a dialog.
 */
export function ReprintReceiptDialog({
  reprint,
  items,
  onClose,
}: {
  /** The duplicate the API answered with, or `null` when none is open. */
  reprint: SaleReprintResource | null;
  /** Catalogue names for the basket's lines, as far as they could be read. */
  items: ReadonlyMap<string, CatalogueItemResource>;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={reprint !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {reprint !== null && (
          <>
            <DialogHeader>
              <DialogTitle>
                Duplicate of <span className="font-mono numeric">{reprint.receiptNumber}</span>
              </DialogTitle>
              <DialogDescription>
                The same receipt, marked a duplicate. No new number was used and nothing about the
                sale has changed.
              </DialogDescription>
            </DialogHeader>

            <Receipt reprint={reprint} items={items} />

            <p className="text-sm text-muted-foreground">
              This reprint has been recorded against your name, as every reprint is.
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The artefact itself, set the way a receipt is set: the number at the top in
 * mono, the basket under it, the total below the rule.
 *
 * **The total is the server's `totalPaise`**, not a sum of these rows. The API
 * redoes that addition and refuses a basket whose lines disagree with it
 * (FR-419), so adding them up here could only ever produce a second opinion —
 * and `docs/ui-conventions.md` is explicit that totals come from the API.
 *
 * No unit price, for the reason `Sale`'s basket gives: `SaleLineResource` carries
 * the quantity and the line's amount and no per-item figure, and dividing one by
 * the other would be this client computing money the API never published.
 */
function Receipt({
  reprint,
  items,
}: {
  reprint: SaleReprintResource;
  items: ReadonlyMap<string, CatalogueItemResource>;
}) {
  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="font-mono text-xl text-foreground numeric">{reprint.receiptNumber}</p>

        {/* Read off the field rather than assumed from the route. `outline` and
            not `destructive`: nothing has been refused, and red in this system
            means the system refused something or is about to. */}
        {reprint.duplicate && <Badge variant="outline">Duplicate</Badge>}
      </div>

      <ul className="space-y-2 border-t border-border pt-4 text-sm">
        {reprint.lines.map((line) => (
          <li key={line.id} className="flex items-baseline justify-between gap-4">
            <span className="text-foreground">
              {items.get(line.catalogueItemId)?.name ?? (
                // Not a blank and not a guess: the name read may have failed, and
                // the id is what somebody quotes when they ask about it.
                <span className="font-mono text-xs numeric">{line.catalogueItemId}</span>
              )}
              <span className="ml-2 font-mono text-muted-foreground numeric">
                × {line.quantity}
              </span>
            </span>
            <span className="font-mono text-foreground numeric">
              {formatPaise(line.amountPaise)}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-baseline justify-between gap-4 border-t border-border pt-4">
        <span className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
          Total
        </span>
        <span className="font-mono text-xl text-foreground numeric">
          {formatPaise(reprint.totalPaise)}
        </span>
      </div>

      {/* The duplicate carries the sale's tax as stored, so a reprinted tax
          invoice reads as the original did — and an unrecorded one says so. */}
      <div className="border-t border-border pt-4 empty:hidden">
        <SaleTaxSection tax={reprint.tax} />
      </div>

      <dl className="space-y-1 text-sm text-muted-foreground">
        <div className="flex justify-between gap-4">
          <dt>Paid by</dt>
          <dd className="text-foreground">
            {labelForPaymentMode(reprint.paymentMode)}
            {reprint.paymentReference !== null && (
              <span className="ml-2 font-mono text-muted-foreground numeric">
                {reprint.paymentReference}
              </span>
            )}
          </dd>
        </div>

        <div className="flex justify-between gap-4">
          <dt>Rung up</dt>
          <dd className="text-foreground">{formatInstant(reprint.occurredAt)}</dd>
        </div>

        {/* A refunded sale is reprintable and its duplicate says so. Hiding it
            would hand somebody a receipt that reads as money the park kept. */}
        {reprint.refundedAt !== null && (
          <div className="flex justify-between gap-4">
            <dt>Refunded</dt>
            <dd className="text-foreground">{formatInstant(reprint.refundedAt)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
